import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type ReportingToEmployeeOption = {
  id: string;
  fullName: string;
  firstName: string;
  lastName: string;
  employeeCode: string;
  departmentName: string;
  designationName: string;
};

type Props = {
  value: string;
  onChange: (employeeId: string) => void;
  employees: ReportingToEmployeeOption[];
  disabled?: boolean;
  placeholder?: string;
  inputClassName?: string;
  orphanLabel?: string | null;
};

type DropdownCoords = {
  top: number;
  left: number;
  width: number;
};

type DepartmentGroup = {
  key: string;
  departmentName: string;
  employees: ReportingToEmployeeOption[];
};

function employeeNameLabel(e: Pick<ReportingToEmployeeOption, "id" | "fullName" | "firstName" | "lastName" | "employeeCode">): string {
  const name = e.fullName.trim() || `${e.firstName} ${e.lastName}`.trim() || `Employee #${e.id}`;
  const code = e.employeeCode.trim();
  return code ? `${name} (${code})` : name;
}

export function employeeReportingLabel(e: ReportingToEmployeeOption): string {
  const name = employeeNameLabel(e);
  const designation = e.designationName.trim();
  return designation ? `${name} · ${designation}` : name;
}

function filterReportingToEmployees(employees: ReportingToEmployeeOption[], search: string): ReportingToEmployeeOption[] {
  const q = search.trim().toLowerCase();
  if (!q) return employees;

  return employees.filter((e) => {
    const haystack = [
      e.fullName,
      e.firstName,
      e.lastName,
      e.employeeCode,
      e.departmentName,
      e.designationName,
      employeeNameLabel(e),
      employeeReportingLabel(e),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

function groupReportingByDepartment(employees: ReportingToEmployeeOption[]): DepartmentGroup[] {
  const byDepartment = new Map<string, ReportingToEmployeeOption[]>();

  for (const employee of employees) {
    const departmentName = employee.departmentName.trim() || "No department";
    const list = byDepartment.get(departmentName) ?? [];
    list.push(employee);
    byDepartment.set(departmentName, list);
  }

  return Array.from(byDepartment.entries())
    .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map(([departmentName, groupEmployees]) => ({
      key: departmentName,
      departmentName,
      employees: groupEmployees
        .slice()
        .sort((a, b) => employeeReportingLabel(a).localeCompare(employeeReportingLabel(b), undefined, { sensitivity: "base" })),
    }));
}

export function ReportingToPicker({
  value,
  onChange,
  employees,
  disabled,
  placeholder = "Search manager…",
  inputClassName,
  orphanLabel,
}: Props) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<DropdownCoords | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => {
    const id = value.trim();
    if (!id) return null;
    return employees.find((e) => String(e.id) === id) ?? null;
  }, [employees, value]);

  const displayValue = useMemo(() => {
    if (!value.trim()) return "";
    if (selected) return employeeReportingLabel(selected);
    if (orphanLabel) return orphanLabel;
    return `Employee #${value.trim()}`;
  }, [orphanLabel, selected, value]);

  const filteredGroups = useMemo(() => {
    const filtered = filterReportingToEmployees(employees, search);
    return groupReportingByDepartment(filtered);
  }, [employees, search]);

  const updatePosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!open || displayValue) {
      setCoords(null);
      return;
    }
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [displayValue, open, updatePosition]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const clearSelection = () => {
    onChange("");
    setSearch("");
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const selectEmployee = (employeeId: string) => {
    onChange(employeeId);
    setSearch("");
    setOpen(false);
  };

  const dropdown =
    open && !displayValue && coords
      ? createPortal(
          <div
            ref={dropdownRef}
            role="listbox"
            style={{ top: coords.top, left: coords.left, width: coords.width }}
            className="fixed z-[9999] max-h-[min(20rem,50vh)] overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
          >
            <ul className="p-1">
              <li>
                <button
                  type="button"
                  role="option"
                  className="w-full rounded-sm px-2 py-2 text-left text-sm text-muted-foreground outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectEmployee("")}
                >
                  None
                </button>
              </li>
            </ul>
            {filteredGroups.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">No matching employees.</div>
            ) : (
              filteredGroups.map((group) => (
                <div key={group.key} className="border-t border-border/40 first:border-t-0">
                  <div className="sticky top-0 z-10 bg-popover px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.departmentName}
                  </div>
                  <ul className="p-1 pt-0">
                    {group.employees.map((employee) => (
                      <li key={employee.id}>
                        <button
                          type="button"
                          role="option"
                          className="w-full rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectEmployee(String(employee.id))}
                        >
                          <span className="font-medium text-foreground">{employeeNameLabel(employee)}</span>
                          {employee.designationName.trim() ? (
                            <span className="text-muted-foreground"> · {employee.designationName.trim()}</span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div ref={containerRef} className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          className={cn(
            "glass-input h-10 w-full pl-9 pr-9 text-sm",
            displayValue && "cursor-pointer bg-muted/25 text-foreground",
            inputClassName,
          )}
          value={displayValue || search}
          readOnly={Boolean(displayValue)}
          disabled={disabled}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          placeholder={displayValue ? "" : placeholder}
          onChange={(e) => {
            if (displayValue) return;
            setSearch(e.target.value);
            setOpen(true);
          }}
          onClick={() => {
            if (!displayValue) return;
            clearSelection();
          }}
          onFocus={() => {
            if (displayValue) return;
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
            if (displayValue && (e.key === "Backspace" || e.key === "Delete")) {
              clearSelection();
            }
          }}
        />
        {displayValue ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            aria-label="Clear manager"
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              clearSelection();
            }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      {dropdown}
    </>
  );
}
